import { Component, EventEmitter, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AppService } from '@app/app.service';
import { CUSTOM_DATE_FORMATS, CustomDatePickerAdapter } from '@app/core/date-adapter';
import { getLanguage } from '@app/core/material.module';
import { TuiDestroyService } from '@taiga-ui/cdk';
import * as moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { FieldConfig, ITrailingButtonEvent } from '../field.interface';

@Component({
    selector: 'app-date',
    templateUrl: './date.component.html',
    styleUrls: ['../input/input.scss', '../date/date.component.scss'],
    providers: [
        TuiDestroyService,
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, deps: [AppService], useFactory: getLanguage },
        { provide: DateAdapter, useClass: CustomDatePickerAdapter, deps: [MAT_DATE_LOCALE] },
    ],
})
export class DateComponent implements OnInit {
    readonly #destroy$: TuiDestroyService = inject(TuiDestroyService);
    field: FieldConfig;
    group: FormGroup;
    data: any;
    max = null;
    min = null;

    /** Событие TrailingButton */
    outputEvent: EventEmitter<ITrailingButtonEvent> = null;

    dataFilter = (d: Date): boolean => {
        if (!this.max && !this.min) {
            return true;
        }
        if (!this.max && this.min) {
            return this.min <= d;
        }
        if (this.max && !this.min) {
            return this.max >= d;
        }
        if (this.max && this.min) {
            return this.max >= d >= this.min;
        }
    };

    constructor(public appService: AppService) {}

    ngOnInit() {
        this.getMax();
        this.getMin();
        if (this.data) {
            if (this.data.maxField && this.group.get(this.data.maxField)) {
                this.group
                    .get(this.data.maxField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMax();
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.maxField).markAsTouched();
                        this.group.get(this.data.maxField).updateValueAndValidity({ emitEvent: false });
                    });
            }
            if (this.data.minField && this.group.get(this.data.minField)) {
                this.group
                    .get(this.data.minField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMin();
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.minField).markAsTouched();
                        this.group.get(this.data.minField).updateValueAndValidity({ emitEvent: false });
                    });
            }
        }

        if (this.field.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
        if (this.field?.disabled) {
            this.setDisabled(true);
        }
    }

    /**
     * Получение максимального ограничения
     */
    getMax() {
        if (this.data && this.data.maxDate) {
            this.max = this.data.maxDate;
        } else if (this.data && this.data.maxField && this.group.get(this.data.maxField)) {
            if (typeof this.group.get(this.data.maxField).value == 'string') {
                this.max = moment(this.group.get(this.data.maxField).value);
            } else {
                this.max = this.group.get(this.data.maxField).value;
            }
        } else {
            this.max = null;
        }
    }

    /**
     * Получение минимального ограничения
     */
    getMin() {
        if (this.data && this.data.minDate) {
            this.min = this.data.minDate;
        } else if (this.data && this.data.minField && this.group.get(this.data.minField)) {
            if (typeof this.group.get(this.data.minField).value == 'string') {
                this.min = moment(this.group.get(this.data.minField).value);
            } else {
                this.min = this.group.get(this.data.minField).value;
            }
        } else {
            this.min = null;
        }
    }

    /**
     * Изменение даты
     */
    changeEvent($event: ClipboardEvent) {
        let ctrlValue = this.group?.get(this.field.name)?.value ? this.group.get(this.field.name).value : null;
        if ($event) {
            ctrlValue = $event?.clipboardData?.getData('text');
        }
        if (typeof ctrlValue === 'string') {
            ctrlValue = moment(ctrlValue, 'DD.MM.YYYY')
                .hour(12)
                .minute(0)
                .second(0)
                .add(moment().utcOffset(), 'm')
                .utc(false);
        }
        if (ctrlValue) {
            if (this.field.name === 'to') {
                this.group
                    ?.get(this.field.name)
                    ?.setValue(
                        moment([ctrlValue.year(), ctrlValue.month(), ctrlValue.date()])
                            .add(0, 'hours')
                            .add(moment().utcOffset(), 'm')
                            .utc(false),
                    );
            } else if (this.field.name === 'from') {
                this.group
                    ?.get(this.field.name)
                    ?.setValue(
                        moment([ctrlValue.year(), ctrlValue.month(), ctrlValue.date()])
                            .add(0, 'hours')
                            .add(moment().utcOffset(), 'm')
                            .utc(false),
                    );
            } else {
                this.group
                    ?.get(this.field.name)
                    ?.setValue(
                        moment([ctrlValue.year(), ctrlValue.month(), ctrlValue.date()])
                            .add(12, 'hours')
                            .add(moment().utcOffset(), 'm')
                            .utc(false),
                    );
            }
        } else {
            this.group?.get(this.field.name)?.setValue(null);
        }
    }

    /**
     * Установка запрета изменения поля
     * @param disabled значение флага
     */
    setDisabled(disabled: boolean) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }
}
